import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle
} from '@bliks-io/ui';

import { CategoryBadge, IconTile } from '@/components/app-console/AppMasterDetail';
import { resolveAppIcon } from '@/lib/app-icon-resolver';
import { resolveAppTitle } from '@/lib/app-loader';
import type { MergedAppRow } from '@/lib/merge-app-catalog';

interface AddTenantAppDialogProps {
  /** Registry apps this tenant does not have yet. */
  candidates: MergedAppRow[];
  /**
   * The app whose add is in flight. The rest wait: the mutation's `variables`
   * name only the latest call, so a second add would take the first one's
   * spinner.
   */
  pendingAppId: string | null;
  /** `onAdded` closes the dialog; call it once the add has succeeded. */
  onAdd: (appId: string, onAdded: () => void) => void;
}

/**
 * Adding puts the app on the tenant; the tenant's own system admin still has to
 * switch it on, which is why nothing here offers to enable it.
 */
export function AddTenantAppDialog({ candidates, pendingAppId, onAdd }: AddTenantAppDialogProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button className="w-full" onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        {t('adminSettings.tenant.addApp')}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && pendingAppId !== null) return;
          setOpen(next);
        }}
      >
        {/* No description: the list speaks for itself, and Radix warns unless
            the omission is declared. */}
        <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('adminSettings.tenant.addApp')}</DialogTitle>
          </DialogHeader>
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('adminSettings.tenant.addAppNoCandidates')}
            </p>
          ) : (
            <div className="max-h-[60vh] divide-y overflow-y-auto">
              {candidates.map((app) => (
                <Item key={app.appId} size="sm" className="items-start gap-3 px-0 pr-1">
                  <ItemMedia>
                    {/* A candidate is neither enabled nor disallowed — the tile
                        carries no state to tint until the app is added. */}
                    <IconTile icon={resolveAppIcon(app.iconName)} status="disabled" size="sm" />
                  </ItemMedia>
                  <ItemContent className="min-w-0">
                    <ItemTitle>
                      {resolveAppTitle(app.appId, app.name, i18n.language)}
                      <CategoryBadge category={app.category} />
                    </ItemTitle>
                    {app.description && <ItemDescription>{app.description}</ItemDescription>}
                  </ItemContent>
                  <ItemActions>
                    <Button
                      size="small"
                      variant="outline"
                      className="shrink-0"
                      loading={pendingAppId === app.appId}
                      disabled={pendingAppId !== null}
                      onClick={() => onAdd(app.appId, () => setOpen(false))}
                    >
                      {t('common.add')}
                    </Button>
                  </ItemActions>
                </Item>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
